import { useTranslation } from 'react-i18next';
import DropdownSelect from 'src/ui/wf/common/select/DropdownSelect';
import Input from 'src/ui/wf/common/input/Input';
import TextArea from 'src/ui/wf/common/textArea/TextArea';
import PhotoGrid from './PhotoGrid';
import {
  WfFloodCategoryStatus,
  getFloodCategoryStatusOptions,
  WfFloodCategoryStatusMapping,
} from 'src/api/wf/models/enums/WfFloodCategoryStatus';
import {
  WfFloodLocation,
  getFloodLocationOptions,
  WfFloodLocationMapping,
  getDefaultFloodLocation,
} from 'src/api/wf/models/enums/WfFloodLocation';
import {
  WfPriority,
  getPriorityOptions,
  WfPriorityStatusMapping,
} from 'src/api/wf/models/enums/WfPriority';

import { FormState } from './hooks';
import { PhotoManager } from './hooks';
import { FormRow } from '../../form/FormRow';

interface FloodRequiredFieldsProps {
  formState: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
  photoManager: PhotoManager;
}

const MAX_PIT_DIMENSION_DIGITS = 4;

export const FloodRequiredFields = ({
  formState,
  onFieldChange,
  photoManager,
}: FloodRequiredFieldsProps) => {
  const { t } = useTranslation();

  // Strips non-digits and caps length so only up to a 4-digit whole number can be entered.
  const handleDimensionChange =
    (field: 'pitDepth' | 'pitWidth' | 'pitLength' | 'waterDepth' | 'estimatedWorkTime') =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, MAX_PIT_DIMENSION_DIGITS);
      onFieldChange(field, digitsOnly === '' ? 0 : Number(digitsOnly));
    };

  return (
    <div className="block space-y-2 border bg-blue-100 p-2">
      <h3 className="mb-4 block text-lg font-semibold">
        {t('wf.statusInformationRegistration.floodStatusRegistration')}
      </h3>

      {/* Category & Location */}
      <FormRow title={t('wf.statusInformationRegistration.floodCategory')}>
        <DropdownSelect
          selectedValue={
            WfFloodCategoryStatusMapping(t)[formState.floodCategory as WfFloodCategoryStatus]
          }
          optionValues={getFloodCategoryStatusOptions(t)}
          onChange={(e) => {
            onFieldChange('floodCategory', e.target.value as WfFloodCategoryStatus);
            onFieldChange(
              'floodLocation',
              getDefaultFloodLocation(e.target.value as WfFloodCategoryStatus)
            );
          }}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.floodLocation')}>
        <DropdownSelect
          selectedValue={WfFloodLocationMapping(t)[formState.floodLocation as WfFloodLocation]}
          optionValues={getFloodLocationOptions(
            formState.floodCategory as WfFloodCategoryStatus,
            t
          )}
          onChange={(e) => onFieldChange('floodLocation', e.target.value as WfFloodLocation)}
        />
      </FormRow>

      {/* Drainage Section */}
      <FormRow title={t('wf.statusInformationRegistration.drainageRequired')}>
        <input
          data-testid="drainageRequired"
          type="checkbox"
          checked={formState.drainageRequired}
          onChange={(e) => onFieldChange('drainageRequired', e.target.checked)}
        />
      </FormRow>
      {formState.drainageRequired &&
        (formState.floodCategory === 'EL' || formState.floodCategory === 'ES') && (
          <div className="space-y-4 border-l-2 border-blue-300 py-4 pl-4">
            <FormRow title={t('wf.statusInformationRegistration.pitDepth')}>
              <Input
                value={formState.pitDepth}
                type="text"
                inputMode="numeric"
                maxLength={MAX_PIT_DIMENSION_DIGITS}
                onChange={handleDimensionChange('pitDepth')}
              />
            </FormRow>
            <FormRow title={t('wf.statusInformationRegistration.pitWidth')}>
              <Input
                value={formState.pitWidth}
                type="text"
                inputMode="numeric"
                maxLength={MAX_PIT_DIMENSION_DIGITS}
                onChange={handleDimensionChange('pitWidth')}
              />
            </FormRow>
            <FormRow title={t('wf.statusInformationRegistration.pitLength')}>
              <Input
                value={formState.pitLength}
                type="text"
                inputMode="numeric"
                maxLength={MAX_PIT_DIMENSION_DIGITS}
                onChange={handleDimensionChange('pitLength')}
              />
            </FormRow>
            <FormRow title={t('wf.statusInformationRegistration.waterDepth')}>
              <Input
                value={formState.waterDepth}
                type="text"
                inputMode="numeric"
                maxLength={MAX_PIT_DIMENSION_DIGITS}
                onChange={handleDimensionChange('waterDepth')}
              />
            </FormRow>
          </div>
        )}

      {/* Priority & Comments */}
      <FormRow title={t('wf.statusInformationRegistration.priority')}>
        <DropdownSelect
          selectedValue={WfPriorityStatusMapping(t)[formState.priority as WfPriority]}
          optionValues={getPriorityOptions(t)}
          onChange={(e) => onFieldChange('priority', Number(e.target.value))}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.estimatedWorkTime')}>
        <Input
          type="text"
          inputMode="numeric"
          maxLength={MAX_PIT_DIMENSION_DIGITS}
          value={formState.estimatedWorkTime}
          onChange={handleDimensionChange('estimatedWorkTime')}
        />
      </FormRow>

      <FormRow title={t('wf.statusInformationRegistration.floodComments')}>
        <TextArea
          value={formState.floodComments ?? ''}
          onChange={(val) => onFieldChange('floodComments', val)}
          rows={3}
        />
      </FormRow>

      {/* Photo Section */}
      <PhotoGrid photoManager={photoManager} themeColor="blue" />
    </div>
  );
};
